import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import * as PIXI from 'pixi.js';
import { Live2DModel } from 'pixi-live2d-display';
import { Face, Vector, Utils } from 'kalidokit';
import { useParticipant } from '@videosdk.live/react-sdk';
import './Live2DViewerMobile.css';

const { lerp } = Vector;
const { clamp } = Utils;

const Live2DViewerMobile = ({ 
  participantId,
  remoteParticipantId = null,
  localAvatarPath = null,
  remoteAvatarPath = null,
  localBackground = null,
  remoteBackground = null,
  availableBackgrounds = []
}) => {
  // State for tracking loading and face detection
  const [localFaceDetected, setLocalFaceDetected] = useState(false);
  const [remoteFaceDetected, setRemoteFaceDetected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [cameraPermissionStatus, setCameraPermissionStatus] = useState('unknown');
  
  // Refs for PIXI and models
  const canvasRef = useRef(null);
  const pixiAppRef = useRef(null);
  const localModelRef = useRef(null);
  const remoteModelRef = useRef(null);
  const localBackgroundSpriteRef = useRef(null);
  const remoteBackgroundSpriteRef = useRef(null);
  
  // Refs for model masks
  const remoteMaskRef = useRef(null);
  const localMaskRef = useRef(null);
  
  // Refs for MediaPipe and face tracking
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localFaceData = useRef(null);
  const remoteFaceData = useRef(null);
  const localFaceMeshRef = useRef(null);
  const remoteFaceMeshRef = useRef(null);
  
  // Get participant data
  const participantData = useParticipant(participantId || "");
  const remoteParticipantData = useParticipant(remoteParticipantId || "");
  
  // Helper function to extract avatar ID from participant metadata
  const getParticipantAvatarId = useCallback((participant) => {
    if (participant?.metaData) {
      try {
        const metadata = typeof participant.metaData === 'string' 
          ? JSON.parse(participant.metaData) 
          : participant.metaData;
        return metadata.avatarId || 'm1';
      } catch (error) {
        console.warn('[Live2DMobile] Error parsing participant metadata:', error);
      }
    }
    return 'm1'; // Default fallback
  }, []);
  
  // Get avatar IDs from participant metadata
  const localAvatarId = useMemo(() => 
    getParticipantAvatarId(participantData), 
    [participantData, getParticipantAvatarId]
  );
  
  const remoteAvatarId = useMemo(() => 
    getParticipantAvatarId(remoteParticipantData), 
    [remoteParticipantData, getParticipantAvatarId]
  );
  
  // Create video streams
  const localVideoStream = useMemo(() => {
    if (participantId && participantData && participantData.webcamOn && participantData.webcamStream) {
      console.log('[Live2DMobile] Creating local video stream for participant:', participantId);
      const mediaStream = new MediaStream();
      mediaStream.addTrack(participantData.webcamStream.track);
      return mediaStream;
    }
    console.log('[Live2DMobile] No local video stream available', {
      participantId,
      webcamOn: participantData?.webcamOn,
      hasStream: !!participantData?.webcamStream
    });
    return null;
  }, [participantId, participantData?.webcamStream, participantData?.webcamOn]);
  
  const remoteVideoStream = useMemo(() => {
    if (remoteParticipantId && remoteParticipantData && remoteParticipantData.webcamOn && remoteParticipantData.webcamStream) {
      console.log('[Live2DMobile] Creating remote video stream for participant:', remoteParticipantId);
      const mediaStream = new MediaStream();
      mediaStream.addTrack(remoteParticipantData.webcamStream.track);
      return mediaStream;
    }
    console.log('[Live2DMobile] No remote video stream available', {
      remoteParticipantId,
      webcamOn: remoteParticipantData?.webcamOn,
      hasStream: !!remoteParticipantData?.webcamStream
    });
    return null;
  }, [remoteParticipantId, remoteParticipantData?.webcamStream, remoteParticipantData?.webcamOn]);

  // Face rigging function
  const rigFace = useCallback((result, model, lerpAmount = 0.7) => {
    if (!model || !result) return;
    
    try {
      const coreModel = model.internalModel.coreModel;
      if (!coreModel) return;

      model.internalModel.eyeBlink = undefined;

      coreModel.setParameterValueById("ParamEyeBallX", lerp(result.pupil.x, coreModel.getParameterValueById("ParamEyeBallX"), lerpAmount));
      coreModel.setParameterValueById("ParamEyeBallY", lerp(result.pupil.y, coreModel.getParameterValueById("ParamEyeBallY"), lerpAmount));

      coreModel.setParameterValueById("ParamAngleX", lerp(result.head.degrees.y, coreModel.getParameterValueById("ParamAngleX"), lerpAmount));
      coreModel.setParameterValueById("ParamAngleY", lerp(result.head.degrees.x, coreModel.getParameterValueById("ParamAngleY"), lerpAmount));
      coreModel.setParameterValueById("ParamAngleZ", lerp(result.head.degrees.z, coreModel.getParameterValueById("ParamAngleZ"), lerpAmount));

      const dampener = 0.2;
      coreModel.setParameterValueById("ParamBodyAngleX", lerp(result.head.degrees.y * dampener, coreModel.getParameterValueById("ParamBodyAngleX"), lerpAmount));
      coreModel.setParameterValueById("ParamBodyAngleY", lerp(result.head.degrees.x * dampener, coreModel.getParameterValueById("ParamBodyAngleY"), lerpAmount));
      coreModel.setParameterValueById("ParamBodyAngleZ", lerp(result.head.degrees.z * dampener, coreModel.getParameterValueById("ParamBodyAngleZ"), lerpAmount));

      let stabilizedEyes = Face.stabilizeBlink(
        {
          l: lerp(result.eye.l, coreModel.getParameterValueById("ParamEyeLOpen"), 0.7),
          r: lerp(result.eye.r, coreModel.getParameterValueById("ParamEyeROpen"), 0.7),
        },
        result.head.y
      );

      coreModel.setParameterValueById("ParamEyeLOpen", stabilizedEyes.l);
      coreModel.setParameterValueById("ParamEyeROpen", stabilizedEyes.r);
      coreModel.setParameterValueById("ParamMouthOpenY", lerp(result.mouth.y, coreModel.getParameterValueById("ParamMouthOpenY"), 0.3));
      coreModel.setParameterValueById("ParamMouthForm", 0.3 + lerp(result.mouth.x, coreModel.getParameterValueById("ParamMouthForm"), 0.3));

      coreModel.update();
    } catch (error) {
      console.warn('[Live2DMobile] Error in rigFace:', error);
    }
  }, []);

  // Setup MediaPipe for face tracking
  const setupMediaPipe = useCallback(async (videoElement, onResults, isLocal = true) => {
    if (!window.FaceMesh || !videoElement) {
      console.error('[Live2DMobile] FaceMesh not available or no video element');
      return null;
    }

    try {
      // Wait longer for mobile devices
      await new Promise(resolve => setTimeout(resolve, isLocal ? 1000 : 1500));
      
      console.log(`[Live2DMobile] Setting up MediaPipe for ${isLocal ? 'local' : 'remote'} on mobile`);
      
      const faceMesh = new window.FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });

      // More relaxed settings for mobile
      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.3, // Lower for mobile
        minTrackingConfidence: 0.3,  // Lower for mobile
      });

      faceMesh.onResults(onResults);
      
      console.log(`[Live2DMobile] MediaPipe setup complete for ${isLocal ? 'local' : 'remote'} mobile`);
      return faceMesh;
    } catch (error) {
      console.error(`[Live2DMobile] Error setting up MediaPipe for ${isLocal ? 'local' : 'remote'} mobile:`, error);
      return null;
    }
  }, []);

  // Get background image URL
  const getBackgroundUrl = useCallback((backgroundId) => {
    if (!backgroundId || !availableBackgrounds) return '/images/backgrounds/bg1.jpg';
    
    const bg = availableBackgrounds.find(bg => bg.id === backgroundId);
    return bg?.preview || '/images/backgrounds/bg1.jpg';
  }, [availableBackgrounds]);

  // Initialize device detection and PIXI application
  useEffect(() => {
    if (!canvasRef.current || isInitialized) return;

    const initializePixi = async () => {
      try {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Create PIXI application
        const app = new PIXI.Application({
          view: canvasRef.current,
          width: screenWidth,
          height: screenHeight,
          transparent: false,
          backgroundColor: 0x000000, // Add black background as fallback
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });
        
        pixiAppRef.current = app;
        setIsInitialized(true);
        
        // Enable sorting by zIndex for proper layering
        app.stage.sortableChildren = true;
        
        setIsInitialized(true);
        
      } catch (error) {
        console.error('[Live2DMobile] Error initializing PIXI:', error);
      }
    };

    initializePixi();

    return () => {
      if (pixiAppRef.current) {
        pixiAppRef.current.destroy(true);
        pixiAppRef.current = null;
      }
      setIsInitialized(false);
    };
  }, []); // Remove the dependencies that were causing re-initialization

  // Handle window resize for mobile orientation changes
  useEffect(() => {
    if (!isInitialized || !pixiAppRef.current) return;

    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      // Resize PIXI app
      pixiAppRef.current.renderer.resize(newWidth, newHeight);
      
      // Update background sizes
      const newHalfHeight = newHeight / 2;
      
      if (remoteBackgroundSpriteRef.current) {
        remoteBackgroundSpriteRef.current.width = newWidth;
        remoteBackgroundSpriteRef.current.height = newHalfHeight;
      }
      
      if (localBackgroundSpriteRef.current) {
        localBackgroundSpriteRef.current.width = newWidth;
        localBackgroundSpriteRef.current.height = newHalfHeight;
        localBackgroundSpriteRef.current.y = newHalfHeight;
      }
      
      // Update model positions, scales and masks with mobile-optimized scaling
      if (remoteModelRef.current) {
        // Update remote model mask with much stricter boundaries to prevent bleeding
        if (remoteMaskRef.current) {
          const topHudHeight = 80; // Account for top UI elements
          remoteMaskRef.current.clear();
          remoteMaskRef.current.beginFill(0xFFFFFF);
          remoteMaskRef.current.drawRect(0, topHudHeight, newWidth, newHalfHeight - topHudHeight - 5); // 5px strict gap from separator
          remoteMaskRef.current.endFill();
        }
        
        // Much more conservative scale factors to prevent bleeding
        const topHudHeight = 80; // Account for top UI elements
        const availableTopHeight = newHalfHeight - topHudHeight - 5; // 5px safety margin from separator
        const remoteScale = Math.min(
          (newWidth * 1.17) / remoteModelRef.current.width,  // Increased by 30% (0.9 * 1.3 = 1.17)
          (availableTopHeight * 1.3) / remoteModelRef.current.height  // Increased by 30% (1.0 * 1.3 = 1.3)
        );
        remoteModelRef.current.scale.set(remoteScale);
        remoteModelRef.current.x = newWidth / 2;
        remoteModelRef.current.y = topHudHeight + (availableTopHeight * 0.7); // Moved down from 0.5 to 0.7
      }
      
      if (localModelRef.current) {
        // Update local model mask with strict boundaries
        if (localMaskRef.current) {
          localMaskRef.current.clear();
          localMaskRef.current.beginFill(0xFFFFFF);
          localMaskRef.current.drawRect(0, newHalfHeight + 5, newWidth, newHalfHeight - 10); // 5px gap from separator, 5px from bottom
          localMaskRef.current.endFill();
        }
        
        // Use conservative scale factors to prevent overlap
        const localScale = Math.min(
          (newWidth * 0.9) / localModelRef.current.width, 
          (newHalfHeight * 1.0) / localModelRef.current.height
        );
        localModelRef.current.scale.set(localScale);
        localModelRef.current.x = newWidth / 2;
        localModelRef.current.y = newHalfHeight + newHalfHeight * 0.5; // Center in bottom half to prevent overlap
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isInitialized]);

  // Load background sprites
  useEffect(() => {
    if (!pixiAppRef.current || !isInitialized) return;

    const loadBackgrounds = async () => {
      try {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const halfHeight = screenHeight / 2;

        // Remove existing background sprites
        if (remoteBackgroundSpriteRef.current) {
          pixiAppRef.current.stage.removeChild(remoteBackgroundSpriteRef.current);
          remoteBackgroundSpriteRef.current.destroy();
        }
        if (localBackgroundSpriteRef.current) {
          pixiAppRef.current.stage.removeChild(localBackgroundSpriteRef.current);
          localBackgroundSpriteRef.current.destroy();
        }

        // Load remote background (top half)
        const remoteBackgroundUrl = getBackgroundUrl(remoteBackground);
        console.log('[Live2DMobile] Loading remote background:', remoteBackgroundUrl);
        const remoteTexture = await PIXI.Texture.fromURL(remoteBackgroundUrl);
        const remoteSprite = new PIXI.Sprite(remoteTexture);
        
        // Scale and position remote background for top half
        remoteSprite.width = screenWidth;
        remoteSprite.height = halfHeight;
        remoteSprite.x = 0;
        remoteSprite.y = 0;
        remoteSprite.zIndex = 1; // Background layer
        
        pixiAppRef.current.stage.addChild(remoteSprite);
        remoteBackgroundSpriteRef.current = remoteSprite;

        // Load local background (bottom half)
        const localBackgroundUrl = getBackgroundUrl(localBackground);
        console.log('[Live2DMobile] Loading local background:', localBackgroundUrl);
        const localTexture = await PIXI.Texture.fromURL(localBackgroundUrl);
        const localSprite = new PIXI.Sprite(localTexture);
        
        // Scale and position local background for bottom half
        localSprite.width = screenWidth;
        localSprite.height = halfHeight;
        localSprite.x = 0;
        localSprite.y = halfHeight;
        localSprite.zIndex = 50; // Background layer
        
        pixiAppRef.current.stage.addChild(localSprite);
        localBackgroundSpriteRef.current = localSprite;

        // Add a visual separator line between the two halves
        const separatorLine = new PIXI.Graphics();
        separatorLine.lineStyle(2, 0x444444, 0.5); // Thin gray line
        separatorLine.moveTo(0, halfHeight);
        separatorLine.lineTo(screenWidth, halfHeight);
        separatorLine.zIndex = 50; // Above everything else
        pixiAppRef.current.stage.addChild(separatorLine);

        // Add aggressive blocking barrier at separator line to prevent remote model bleeding
        const separatorBarrier = new PIXI.Graphics();
        separatorBarrier.beginFill(0x000000, 0.0); // Invisible but functional barrier
        separatorBarrier.drawRect(0, halfHeight - 10, screenWidth, 20); // 20px tall barrier at separator
        separatorBarrier.zIndex = 60; // Above models and separator
        pixiAppRef.current.stage.addChild(separatorBarrier);

        // Add blocking overlays to ensure no content bleeds through
        // Top blocking overlay (covers anything that might leak from bottom)
        const topBlocker = new PIXI.Graphics();
        topBlocker.beginFill(0x000000, 0.01); // Almost transparent black
        topBlocker.drawRect(0, -100, screenWidth, 100); // Block area above top half
        topBlocker.zIndex = 15; // Above models but below UI
        pixiAppRef.current.stage.addChild(topBlocker);
        
        // Bottom blocking overlay (covers anything that might leak from top)
        const bottomBlocker = new PIXI.Graphics();
        bottomBlocker.beginFill(0x000000, 0.01); // Almost transparent black
        bottomBlocker.drawRect(0, screenHeight, screenWidth, 100); // Block area below bottom half
        bottomBlocker.zIndex = 15; // Above models but below UI
        pixiAppRef.current.stage.addChild(bottomBlocker);

        // Enable sorting by zIndex
        pixiAppRef.current.stage.sortableChildren = true;

        console.log('[Live2DMobile] Background images loaded successfully');

      } catch (error) {
        console.error('[Live2DMobile] Error loading backgrounds:', error);
      }
    };

    loadBackgrounds();
  }, [isInitialized, remoteBackground, localBackground, getBackgroundUrl]); // Fixed dependencies for background changes

  // Load models
  useEffect(() => {
    if (!pixiAppRef.current || !isInitialized) return;

    const loadModels = async () => {
      try {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const halfHeight = screenHeight / 2;
        const topHudHeight = 80; // Account for top UI elements - define once for the entire function

        console.log('[Live2DMobile] Starting model loading...', {
          screenWidth, screenHeight, halfHeight,
          remoteAvatarPath, localAvatarPath
        });

        // Load models sequentially to avoid conflicts
        // Load remote model first
        if (remoteAvatarPath) {
          try {
            // Remove existing remote model
            if (remoteModelRef.current) {
              pixiAppRef.current.stage.removeChild(remoteModelRef.current);
              remoteModelRef.current.destroy();
              remoteModelRef.current = null;
            }

            console.log('[Live2DMobile] Loading remote model:', remoteAvatarPath);
            const remoteModel = await Live2DModel.from(remoteAvatarPath);
            
            if (!remoteModel) {
              console.error('[Live2DMobile] Remote model failed to load');
            } else {
              // Create a container for the remote model to ensure proper masking
              const remoteContainer = new PIXI.Container();
              
              // Create mask for top half with much stricter boundaries to prevent bleeding
              const topMask = new PIXI.Graphics();
              topMask.beginFill(0xFFFFFF);
              topMask.drawRect(0, topHudHeight, screenWidth, halfHeight - topHudHeight - 5); // 5px strict gap from separator
              topMask.endFill();
              remoteMaskRef.current = topMask; // Store mask reference
              
              // Apply mask to the container, not the model - this is critical for proper clipping
              // remoteContainer.mask = topMask;
              remoteContainer.zIndex = 10; // Container above backgrounds but below UI
              
              // Add mask to stage first, then container
              pixiAppRef.current.stage.addChild(topMask);
              pixiAppRef.current.stage.addChild(remoteContainer);
              
              // Much more conservative scaling and positioning to prevent bleeding
              const availableTopHeight = halfHeight - topHudHeight - 5; // 5px safety margin from separator
              const remoteScale = Math.min(
                (screenWidth * 1.17) / remoteModel.width,  // Increased by 30% (0.9 * 1.3 = 1.17)
                (availableTopHeight * 1.3) / remoteModel.height  // Increased by 30% (1.0 * 1.3 = 1.3)
              );
              remoteModel.scale.set(remoteScale);
              remoteModel.x = screenWidth / 2;
              remoteModel.y = topHudHeight + (availableTopHeight * 0.7); // Moved down from 0.5 to 0.7
              remoteModel.anchor.set(0.5, 0.5);

              remoteModel.mask = topMask;
              
              // Add model to container with proper z-indexing
              remoteContainer.addChild(remoteModel);
              remoteModelRef.current = remoteModel;
              
              console.log('[Live2DMobile] Remote model loaded successfully', {
                scale: remoteScale,
                position: { x: remoteModel.x, y: remoteModel.y },
                stageChildren: pixiAppRef.current.stage.children.length,
                maskApplied: !!remoteContainer.mask
              });
            }
          } catch (error) {
            console.error('[Live2DMobile] Error loading remote model:', error);
          }
        } else if (process.env.NODE_ENV === 'development' && localAvatarPath) {
          // Development mode: Load a test remote avatar if no remote participant
          try {
            console.log('[Live2DMobile] Loading test remote model for development');
            const testRemoteModel = await Live2DModel.from('/live2d/models/IceGirl_Live2D/IceGirl.model3.json');
            
            if (testRemoteModel) {
              // Create a container for the test remote model to ensure proper masking
              const testRemoteContainer = new PIXI.Container();
              
              // Create mask for top half with much stricter boundaries to prevent bleeding
              const topMask = new PIXI.Graphics();
              topMask.beginFill(0xFFFFFF);
              topMask.drawRect(0, topHudHeight, screenWidth, halfHeight - topHudHeight - 5); // 5px strict gap from separator
              topMask.endFill();
              remoteMaskRef.current = topMask; // Store mask reference
              
              // Apply mask to the container, not the model - this is critical for proper clipping
              testRemoteContainer.mask = topMask;
              testRemoteContainer.zIndex = 10; // Container above backgrounds but below UI
              
              // Add mask to stage first, then container
              pixiAppRef.current.stage.addChild(topMask);
              pixiAppRef.current.stage.addChild(testRemoteContainer);
              
              // Much more conservative scaling and positioning for test model to prevent bleeding
              const availableTopHeight = halfHeight - topHudHeight - 5; // 5px safety margin from separator
              const remoteScale = Math.min(
                (screenWidth * 1.17) / testRemoteModel.width,  // Increased by 30% (0.9 * 1.3 = 1.17)
                (availableTopHeight * 1.3) / testRemoteModel.height  // Increased by 30% (1.0 * 1.3 = 1.3)
              );
              testRemoteModel.scale.set(remoteScale);
              testRemoteModel.x = screenWidth / 2;
              testRemoteModel.y = topHudHeight + (availableTopHeight * 0.7); // Moved down from 0.5 to 0.7
              testRemoteModel.anchor.set(0.5, 0.5);
              
              // Add model to container with proper z-indexing
              testRemoteContainer.addChild(testRemoteModel);
              remoteModelRef.current = testRemoteModel;
              
              console.log('[Live2DMobile] Test remote model loaded for development');
            }
          } catch (error) {
            console.log('[Live2DMobile] Test remote model not available, skipping');
          }
        }

        // Wait a bit then load local model to ensure remote model is stable
        await new Promise(resolve => setTimeout(resolve, 500));

        // Load local model
        if (localAvatarPath) {
          try {
            // Remove existing local model
            if (localModelRef.current) {
              pixiAppRef.current.stage.removeChild(localModelRef.current);
              localModelRef.current.destroy();
              localModelRef.current = null;
            }

            console.log('[Live2DMobile] Loading local model:', localAvatarPath);
            console.log('[Live2DMobile] Stage children before local load:', pixiAppRef.current.stage.children.length);
            
            const localModel = await Live2DModel.from(localAvatarPath);
            
            if (!localModel) {
              console.error('[Live2DMobile] Local model failed to load');
            } else {
              // Create a container for the local model to ensure proper masking
              const localContainer = new PIXI.Container();
              
              // Create mask for bottom half to prevent overlap with strict boundaries
              const bottomMask = new PIXI.Graphics();
              bottomMask.beginFill(0xFFFFFF);
              bottomMask.drawRect(0, halfHeight + 5, screenWidth, halfHeight - 10); // 5px gap from separator, 5px from bottom
              bottomMask.endFill();
              localMaskRef.current = bottomMask; // Store mask reference
              
              // Scale and position for bottom half with mobile-optimized scaling
              const localScale = Math.min(
                (screenWidth * 0.9) / localModel.width, 
                (halfHeight * 1.0) / localModel.height
              );
              localModel.scale.set(localScale);
              localModel.x = screenWidth / 2;
              localModel.y = halfHeight + halfHeight * 0.5; // Center in bottom half to prevent overlap
              localModel.anchor.set(0.5, 0.5);
              
              // Apply mask to the container, not the model - critical for proper clipping
              localContainer.mask = bottomMask;
              localContainer.zIndex = 51; // Local container slightly above remote to ensure proper layering
              
              // Add mask to stage first, then container
              pixiAppRef.current.stage.addChild(bottomMask);
              pixiAppRef.current.stage.addChild(localContainer);

              // Add model to container with proper z-indexing
              localContainer.addChild(localModel);
              localModelRef.current = localModel;
              
              console.log('[Live2DMobile] Local model loaded successfully', {
                scale: localScale,
                position: { x: localModel.x, y: localModel.y },
                stageChildren: pixiAppRef.current.stage.children.length
              });
              
              // Verify remote model is still there
              console.log('[Live2DMobile] Remote model still exists?', !!remoteModelRef.current);
              console.log('[Live2DMobile] Remote model in stage?', 
                remoteModelRef.current ? pixiAppRef.current.stage.children.includes(remoteModelRef.current) : false);
            }
          } catch (error) {
            console.error('[Live2DMobile] Error loading local model:', error);
          }
        }

      } catch (error) {
        console.error('[Live2DMobile] Error in model loading process:', error);
      }
    };

    loadModels();
  }, [pixiAppRef.current, isInitialized, remoteAvatarPath, localAvatarPath]);

  // Setup face tracking for remote user
  useEffect(() => {
    if (!remoteVideoStream) {
      console.log('[Live2DMobile] No remote video stream for face tracking');
      return;
    }

    const setupRemoteFaceTracking = async () => {
      try {
        console.log('[Live2DMobile] Setting up remote face tracking for mobile...');
        const videoElement = document.createElement('video');
        videoElement.srcObject = remoteVideoStream;
        videoElement.autoplay = true;
        videoElement.muted = true;
        videoElement.playsInline = true; // Critical for mobile
        videoElement.controls = false;
        videoElement.style.display = 'none';
        
        // Mobile-specific video attributes
        videoElement.setAttribute('webkit-playsinline', 'true');
        videoElement.setAttribute('playsinline', 'true');
        
        document.body.appendChild(videoElement);
        remoteVideoRef.current = videoElement;

        // Wait for video to be ready with timeout
        await Promise.race([
          new Promise((resolve) => {
            videoElement.onloadedmetadata = () => {
              console.log('[Live2DMobile] Remote video metadata loaded');
              resolve();
            };
          }),
          new Promise((resolve) => setTimeout(resolve, 5000)) // 5 second timeout
        ]);

        // Ensure video is playing
        try {
          await videoElement.play();
          console.log('[Live2DMobile] Remote video playing');
        } catch (playError) {
          console.warn('[Live2DMobile] Remote video play error (might be normal):', playError);
        }

        const onRemoteResults = (results) => {
          if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
            const landmarks = results.multiFaceLandmarks[0];
            try {
              remoteFaceData.current = Face.solve(landmarks, {
                runtime: 'mediapipe',
                video: videoElement,
              });
              setRemoteFaceDetected(true);
            } catch (faceError) {
              console.warn('[Live2DMobile] Error solving remote face:', faceError);
            }
          } else {
            setRemoteFaceDetected(false);
          }
        };

        const faceMesh = await setupMediaPipe(videoElement, onRemoteResults, false);
        if (faceMesh) {
          remoteFaceMeshRef.current = faceMesh;

          // Start processing with mobile-optimized frame rate
          let frameCount = 0;
          const processFrame = async () => {
            frameCount++;
            // Process every 3rd frame on mobile for better performance
            if (frameCount % 3 === 0 && videoElement.readyState >= 2 && faceMesh) {
              try {
                await faceMesh.send({ image: videoElement });
              } catch (processError) {
                console.warn('[Live2DMobile] Frame processing error:', processError);
              }
            }
            requestAnimationFrame(processFrame);
          };
          processFrame();
          console.log('[Live2DMobile] Remote face tracking started on mobile');
        }

      } catch (error) {
        console.error('[Live2DMobile] Error setting up remote face tracking on mobile:', error);
      }
    };

    setupRemoteFaceTracking();

    return () => {
      if (remoteVideoRef.current) {
        if (remoteVideoRef.current.srcObject) {
          remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
        if (document.body.contains(remoteVideoRef.current)) {
          document.body.removeChild(remoteVideoRef.current);
        }
        remoteVideoRef.current = null;
      }
      if (remoteFaceMeshRef.current) {
        remoteFaceMeshRef.current.close();
        remoteFaceMeshRef.current = null;
      }
    };
  }, [remoteVideoStream, setupMediaPipe]);

  // Setup face tracking for local user
  useEffect(() => {
    if (!localVideoStream) {
      console.log('[Live2DMobile] No local video stream for face tracking');
      return;
    }

    const checkCameraPermissions = async () => {
      try {
        if (navigator.permissions) {
          const permission = await navigator.permissions.query({ name: 'camera' });
          console.log('📷 Camera permission status:', permission.state);
          setCameraPermissionStatus(permission.state);
          
          permission.onchange = () => {
            console.log('📷 Camera permission changed to:', permission.state);
            setCameraPermissionStatus(permission.state);
          };
        } else {
          // Fallback: Try to request camera access directly
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
              video: { 
                facingMode: 'user',
                width: { ideal: 640 },
                height: { ideal: 480 }
              } 
            });
            console.log('📷 Camera access test successful');
            setCameraPermissionStatus('granted');
            stream.getTracks().forEach(track => track.stop()); // Stop test stream
          } catch (error) {
            console.warn('📷 Camera access test failed:', error);
            setCameraPermissionStatus('denied');
          }
        }
      } catch (error) {
        console.warn('📷 Could not check camera permissions:', error);
        setCameraPermissionStatus('unknown');
      }
    };

    const setupLocalFaceTracking = async () => {
      try {
        // Longer delay for mobile to ensure remote tracking is stable
        await new Promise(resolve => setTimeout(resolve, 2500));
        
        console.log('[Live2DMobile] Setting up local face tracking for mobile...');
        const videoElement = document.createElement('video');
        videoElement.srcObject = localVideoStream;
        videoElement.autoplay = true;
        videoElement.muted = true;
        videoElement.playsInline = true; // Critical for mobile
        videoElement.controls = false;
        videoElement.style.display = 'none';
        
        // Mobile-specific video attributes
        videoElement.setAttribute('webkit-playsinline', 'true');
        videoElement.setAttribute('playsinline', 'true');
        
        document.body.appendChild(videoElement);
        localVideoRef.current = videoElement;

        // Wait for video to be ready with timeout
        await Promise.race([
          new Promise((resolve) => {
            videoElement.onloadedmetadata = () => {
              console.log('[Live2DMobile] Local video metadata loaded');
              resolve();
            };
          }),
          new Promise((resolve) => setTimeout(resolve, 5000)) // 5 second timeout
        ]);

        // Ensure video is playing
        try {
          await videoElement.play();
          console.log('[Live2DMobile] Local video playing');
        } catch (playError) {
          console.warn('[Live2DMobile] Local video play error (might be normal):', playError);
        }

        const onLocalResults = (results) => {
          if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
            const landmarks = results.multiFaceLandmarks[0];
            try {
              localFaceData.current = Face.solve(landmarks, {
                runtime: 'mediapipe',
                video: videoElement,
              });
              setLocalFaceDetected(true);
            } catch (faceError) {
              console.warn('[Live2DMobile] Error solving local face:', faceError);
            }
          } else {
            setLocalFaceDetected(false);
          }
        };

        const faceMesh = await setupMediaPipe(videoElement, onLocalResults, true);
        if (faceMesh) {
          localFaceMeshRef.current = faceMesh;

          // Start processing with mobile-optimized frame rate
          let frameCount = 0;
          const processFrame = async () => {
            frameCount++;
            // Process every 3rd frame on mobile for better performance
            if (frameCount % 3 === 0 && videoElement.readyState >= 2 && faceMesh) {
              try {
                await faceMesh.send({ image: videoElement });
              } catch (processError) {
                console.warn('[Live2DMobile] Frame processing error:', processError);
              }
            }
            requestAnimationFrame(processFrame);
          };
          processFrame();
          console.log('[Live2DMobile] Local face tracking started on mobile');
        }

      } catch (error) {
        console.error('[Live2DMobile] Error setting up local face tracking on mobile:', error);
      }
    };

    // Check permissions first, then setup face tracking
    checkCameraPermissions().then(() => {
      setupLocalFaceTracking();
    });

    return () => {
      if (localVideoRef.current) {
        if (localVideoRef.current.srcObject) {
          localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
        if (document.body.contains(localVideoRef.current)) {
          document.body.removeChild(localVideoRef.current);
        }
        localVideoRef.current = null;
      }
      if (localFaceMeshRef.current) {
        localFaceMeshRef.current.close();
        localFaceMeshRef.current = null;
      }
    };
  }, [localVideoStream, setupMediaPipe]);

  // Animation loop
  useEffect(() => {
    if (!isInitialized) return;

    console.log('[Live2DMobile] Starting animation loop for mobile');
    let animationId;
    let frameCount = 0;

    const animate = () => {
      frameCount++;
      
      // Throttle animation on mobile for better performance (every 2nd frame)
      if (frameCount % 2 === 0) {
        // Animate remote model
        if (remoteFaceData.current && remoteModelRef.current) {
          try {
            rigFace(remoteFaceData.current, remoteModelRef.current);
          } catch (error) {
            console.warn('[Live2DMobile] Remote face rigging error:', error);
          }
        }

        // Animate local model
        if (localFaceData.current && localModelRef.current) {
          try {
            rigFace(localFaceData.current, localModelRef.current);
          } catch (error) {
            console.warn('[Live2DMobile] Local face rigging error:', error);
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isInitialized, rigFace]);

  // Add bounds validation for debugging (can be removed in production)
  useEffect(() => {
    if (!isInitialized) return;

    const validateBounds = () => {
      const screenHeight = window.innerHeight;
      const halfHeight = screenHeight / 2;
      const topHudHeight = 80; // Account for top UI elements

      // Check remote model bounds (should stay below HUD and above separator)
      if (remoteModelRef.current) {
        const remoteBounds = remoteModelRef.current.getBounds();
        if (remoteBounds.top < topHudHeight) {
          console.warn('[Live2DMobile] Remote model extending into HUD area!', {
            top: remoteBounds.top,
            topHudHeight: topHudHeight,
            overflow: topHudHeight - remoteBounds.top
          });
        }
        if (remoteBounds.bottom > halfHeight + 10) { // Allow small tolerance
          console.warn('[Live2DMobile] Remote model exceeding bottom boundary!', {
            bottom: remoteBounds.bottom,
            halfHeight: halfHeight,
            overflow: remoteBounds.bottom - halfHeight
          });
        }
      }

      // Check local model bounds
      if (localModelRef.current) {
        const localBounds = localModelRef.current.getBounds();
        if (localBounds.top < halfHeight - 10) { // Allow small tolerance
          console.warn('[Live2DMobile] Local model exceeding top boundary!', {
            top: localBounds.top,
            halfHeight: halfHeight,
            overflow: halfHeight - localBounds.top
          });
        }
      }
    };

    const boundsInterval = setInterval(validateBounds, 5000); // Check every 5 seconds
    return () => clearInterval(boundsInterval);
  }, [isInitialized]);

  return (
    <div className="live2d-mobile-viewer">
      <canvas 
        ref={canvasRef}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 10,
          pointerEvents: 'auto'
        }}
      />
    </div>
  );
};

export default Live2DViewerMobile;
